// client/src/Admin.jsx
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import {
  FaTimes,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaChevronDown,
  FaChevronUp,
  FaUserCheck,
  FaUserTimes,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import "./Style.css";
import { BackOfficeHeader } from "./GameSessionHeader";
import GameTutorialModal from "./GameTutorialModal";

const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  const input = phone.replace(/\D/g, "");
  if (input.length > 3 && input.length <= 6) {
    return `${input.slice(0, 3)}-${input.slice(3)}`;
  } else if (input.length > 6) {
    return `${input.slice(0, 3)}-${input.slice(3, 6)}-${input.slice(6, 10)}`;
  }
  return input;
};

const PERMISSION_GROUPS = [
  {
    label: "Backend",
    permissions: [
      { key: "V", label: "Access Backend" },
      { key: "S", label: "Manage Settings", requiresBackend: true },
      { key: "U", label: "Manage and Invite Users", requiresBackend: true },
      { key: "A", label: "Manage Agents", requiresBackend: true },
      { key: "C", label: "Modify Credit Limits", requiresBackend: true },
      { key: "H", label: "Modify Schedule", requiresBackend: true },
    ],
  },
  {
    label: "Game Operations",
    permissions: [
      { key: "W", label: "Add Wallet Transactions" },
      { key: "P", label: "Edit Players" },
      { key: "D", label: "Delete Players" },
      { key: "T", label: "Record Book Transactions" },
      { key: "B", label: "View Book Values" },
    ],
  },
];

const TOURNAMENT_ENABLED_KEY = "O";
const TOURNAMENT_ONLY_KEY = "Y";
const ALL_PERMISSIONS = PERMISSION_GROUPS.flatMap((group) => group.permissions);
const BACKEND_DEPENDENT_KEYS = ALL_PERMISSIONS.filter(
  (permission) => permission.requiresBackend,
).map((permission) => permission.key);

const SETTINGS_MODAL_SECTIONS = [
  {
    id: "settings-cash",
    label: "Cash",
    description: "",
  },
  {
    id: "settings-tournaments",
    label: "Tournaments",
    description: "",
  },
  {
    id: "settings-nexus-mobile",
    label: "Nexus Mobile",
    description: "",
  },
  {
    id: "settings-general",
    label: "General",
    description: "",
  },
];

const normalizePermissions = (permissions, dealerMode) => {
  const uniquePermissions = Array.from(
    new Set((permissions || "").split("").filter(Boolean)),
  );
  if (Number(dealerMode) === 2) return "";
  if (uniquePermissions.includes(TOURNAMENT_ONLY_KEY)) {
    return TOURNAMENT_ONLY_KEY;
  }
  if (!uniquePermissions.includes("V")) {
    return uniquePermissions
      .filter((key) => !BACKEND_DEPENDENT_KEYS.includes(key))
      .join("");
  }
  return uniquePermissions.join("");
};

const INVITE_ROLE_DETAILS = {
  staff: {
    title: "Staff",
    permissions:
      "Game operations access: add wallet transactions, edit players, delete players, record book transactions, and view book values.",
  },
  admin: {
    title: "Admin",
    permissions:
      "Staff access plus backend access. More admin permissions can be enabled later in Access Control.",
  },
  dealer: {
    title: "Dealer",
    permissions:
      "Dealer-only access. Backend and game operation permissions start disabled.",
  },
  tournament: {
    title: "Tournament",
    permissions:
      "Tournament-only access. The user starts on tournaments and game operations are hidden.",
  },
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
const CARDROOM_IMAGE_OUTPUT_SIZE = 720;
const ANNOUNCEMENT_IMAGE_OUTPUT_WIDTH = 900;
const ANNOUNCEMENT_IMAGE_OUTPUT_HEIGHT = 620;
const MAPBOX_SEARCH_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

const createMapboxSessionToken = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getMapboxSuggestionLabel = (suggestion) =>
  suggestion?.full_address ||
  [suggestion?.name, suggestion?.place_formatted].filter(Boolean).join(", ") ||
  suggestion?.address ||
  "";

const normalizeMapboxStateCode = (region) => {
  const raw =
    region?.region_code ||
    region?.short_code ||
    region?.region_code_full ||
    region?.name ||
    "";
  const upper = String(raw).trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(upper)) return upper;
  const match = upper.match(/[A-Z]{2}$/);
  return match ? match[0] : "";
};

const getMapboxContextName = (context, keys) => {
  for (const key of keys) {
    if (context?.[key]?.name) return context[key].name;
  }
  return "";
};

const parseMapboxAddressFeature = (feature, fallbackSuggestion) => {
  const properties = feature?.properties || {};
  const context = properties.context || {};
  const coordinates = feature?.geometry?.coordinates || [];
  const address =
    properties.full_address ||
    getMapboxSuggestionLabel(fallbackSuggestion) ||
    [properties.name, properties.place_formatted].filter(Boolean).join(", ");

  return {
    address,
    city: getMapboxContextName(context, ["place", "locality", "district"]),
    state: normalizeMapboxStateCode(context.region),
    lat: Number.isFinite(Number(coordinates[1])) ? Number(coordinates[1]) : null,
    lng: Number.isFinite(Number(coordinates[0])) ? Number(coordinates[0]) : null,
  };
};

const getDiscoverLocationLock = (changedAt) => {
  if (!changedAt) return { locked: false, unlocksAt: null };
  const changedTime = new Date(changedAt).getTime();
  if (!Number.isFinite(changedTime)) return { locked: false, unlocksAt: null };
  const unlocksAt = new Date(changedTime + 24 * 60 * 60 * 1000);
  return { locked: Date.now() < unlocksAt.getTime(), unlocksAt };
};

const formatShortDateTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const normalizeTournamentDefaultTables = (tables) => {
  const names = Array.isArray(tables)
    ? tables.map((name) => String(name || "").trim()).filter(Boolean)
    : [];
  return names.length ? names.slice(0, 4) : ["Table 1"];
};

const tournamentDefaultTablesEqual = (a, b) =>
  JSON.stringify(normalizeTournamentDefaultTables(a)) ===
  JSON.stringify(normalizeTournamentDefaultTables(b));

function MapboxAddressInput({
  value,
  selectedAddress,
  disabled = false,
  disabledReason = "",
  onChange,
  onAddressSelect,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const sessionTokenRef = useRef(createMapboxSessionToken());

  useEffect(() => {
    if (disabled) {
      setOptions([]);
      setLoading(false);
      setError("");
      return;
    }

    const query = value.trim();
    if (query === String(selectedAddress || "").trim()) {
      setOptions([]);
      setLoading(false);
      setError("");
      return;
    }

    if (query.length < 3) {
      setOptions([]);
      setLoading(false);
      setError("");
      return;
    }

    if (!MAPBOX_SEARCH_TOKEN) {
      setOptions([]);
      setLoading(false);
      setError("Set REACT_APP_MAPBOX_TOKEN to enable address search.");
      return;
    }

    let cancelled = false;
    const timeout = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          q: query,
          access_token: MAPBOX_SEARCH_TOKEN,
          session_token: sessionTokenRef.current,
          types: "address",
          country: "us",
          limit: "5",
        });
        const response = await fetch(
          `https://api.mapbox.com/search/searchbox/v1/suggest?${params.toString()}`,
        );
        const payload = await response.json();

        if (!response.ok) {
          throw new Error(payload?.message || "Could not load address suggestions");
        }

        if (!cancelled) setOptions(payload.suggestions || []);
      } catch (err) {
        if (!cancelled) {
          setOptions([]);
          setError(err.message || "Could not load address suggestions");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [value, selectedAddress, disabled]);

  const handleSelect = async (suggestion) => {
    if (disabled || !MAPBOX_SEARCH_TOKEN || !suggestion?.mapbox_id) return;

    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        access_token: MAPBOX_SEARCH_TOKEN,
        session_token: sessionTokenRef.current,
      });
      const response = await fetch(
        `https://api.mapbox.com/search/searchbox/v1/retrieve/${encodeURIComponent(
          suggestion.mapbox_id,
        )}?${params.toString()}`,
      );
      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload?.message || "Could not load that address");
      }

      const parsed = parseMapboxAddressFeature(payload.features?.[0], suggestion);
      if (!parsed.address || !parsed.state || parsed.lat === null || parsed.lng === null) {
        throw new Error("Could not read the selected address details.");
      }

      onAddressSelect(parsed);
      setOptions([]);
      sessionTokenRef.current = createMapboxSessionToken();
    } catch (err) {
      setError(err.message || "Could not load that address");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <input
        type="text"
        className="game-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder="Start typing the public address"
        style={{
          width: "100%",
          boxSizing: "border-box",
          opacity: disabled ? 0.62 : 1,
          cursor: disabled ? "not-allowed" : "text",
        }}
      />
      {disabled && disabledReason && (
        <p
          className="game-text-secondary"
          style={{ margin: "0.4rem 0 0", fontSize: "0.76rem" }}
        >
          {disabledReason}
        </p>
      )}
      {(loading || options.length > 0 || error) && (
        <div
          style={{
            marginTop: "0.45rem",
            border: "1px solid rgba(255,255,255,0.22)",
            borderRadius: "8px",
            background: "var(--game-bg-primary)",
            boxShadow: "0 14px 28px rgba(0,0,0,0.35)",
            overflow: "hidden",
          }}
        >
          {loading && (
            <div className="game-text-secondary" style={{ padding: "0.65rem 0.75rem" }}>
              Searching addresses...
            </div>
          )}
          {error && (
            <div className="game-text-danger" style={{ padding: "0.65rem 0.75rem" }}>
              {error}
            </div>
          )}
          {!loading &&
            options.map((option) => (
              <button
                key={option.mapbox_id}
                type="button"
                onClick={() => handleSelect(option)}
                style={{
                  width: "100%",
                  padding: "0.85rem 0.9rem",
                  textAlign: "left",
                  border: "none",
                  borderBottom: "1px solid var(--game-border-light)",
                  background: "var(--game-bg-secondary)",
                  color: "var(--game-text-primary)",
                  cursor: "pointer",
                  fontWeight: 600,
                  lineHeight: 1.35,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.07)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "var(--game-bg-secondary)";
                }}
              >
                {getMapboxSuggestionLabel(option)}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const { cardroom } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);
  const user = localStorage.getItem("user_name");
  let currentUserId = Number(localStorage.getItem("user_id") || 0);
  if (!currentUserId && token) {
    try {
      currentUserId = Number(jwtDecode(token).user_id || 0);
    } catch (err) {
      currentUserId = 0;
    }
  }

  // Invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [inviteSuccess, setInviteSuccess] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [usersView, setUsersView] = useState("access");
  const [savingAccess, setSavingAccess] = useState({});
  const [accessErrors, setAccessErrors] = useState({});

  // Message form
  const [message, setMessage] = useState("");
  const [messageError, setMessageError] = useState("");
  const [messageSuccess, setMessageSuccess] = useState("");
  const [supportMessages, setSupportMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  // Cardroom data
  const [cardroomData, setCardroomData] = useState(null);
  const [originalCardroomData, setOriginalCardroomData] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [cardroomImageCrop, setCardroomImageCrop] = useState(null);
  const [cardroomImageError, setCardroomImageError] = useState("");
  const cardroomImageCanvasRef = useRef(null);
  const [announcementImageCrop, setAnnouncementImageCrop] = useState(null);
  const [announcementImageError, setAnnouncementImageError] = useState("");
  const announcementImageCanvasRef = useRef(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showTutorialModal, setShowTutorialModal] = useState(false);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [membershipPortalLoading, setMembershipPortalLoading] = useState(false);
  const [membershipPortalError, setMembershipPortalError] = useState("");
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [reviewsData, setReviewsData] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState("");
  const [discoverAddressQuery, setDiscoverAddressQuery] = useState("");
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [newAccessCode, setNewAccessCode] = useState("");
  const [newIsOpen, setNewIsOpen] = useState(true);
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  const [codeAvailable, setCodeAvailable] = useState(true);
  const [networkLoading, setNetworkLoading] = useState(false);

  // Player invites
  const [playerInvites, setPlayerInvites] = useState([]);
  const [isInvitesCollapsed, setIsInvitesCollapsed] = useState(false);

  // Invite action modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteModalAction, setInviteModalAction] = useState("");
  const [inviteModalPlayerId, setInviteModalPlayerId] = useState(null);
  const [inviteModalPlayerName, setInviteModalPlayerName] = useState("");
  const [inviteModalMessage, setInviteModalMessage] = useState("");
  const [userDeleteTarget, setUserDeleteTarget] = useState(null);
  const [userDeleteLoading, setUserDeleteLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate(`/`);
      return;
    }
    if (dealerMode === 2) {
      navigate(`/${cardroom}/dealer_dashboard`);
      return;
    } else if (!permission?.includes("V")) {
      navigate("/");
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }, [token, cardroom, navigate, dealerMode, permission]);

  useEffect(() => {
    if (token) {
      fetchUsers();
      fetchMessages();
      fetchCardroom();
      fetchPlayerInvites();
    }
  }, [token]);

  const fetchCardroom = async () => {
    try {
      const { data } = await axios.get(`/${cardroom}/api/cardroom`);
      const normalizedData = {
        ...data,
        tournamentDefaultTables: normalizeTournamentDefaultTables(
          data.tournamentDefaultTables,
        ),
      };
      setCardroomData(normalizedData);
      setOriginalCardroomData(normalizedData);
      setDiscoverAddressQuery(data.discoverAddress || "");
    } catch (err) {
      console.error("Failed to fetch cardroom data:", err);
    }
  };

  const drawCardroomImageCrop = (returnDataUrl = false) => {
    const canvas = cardroomImageCanvasRef.current;
    if (!canvas || !cardroomImageCrop?.image) return null;

    const size = CARDROOM_IMAGE_OUTPUT_SIZE;
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#111827";
    ctx.fillRect(0, 0, size, size);

    const { image, naturalWidth, naturalHeight, zoom, offsetX, offsetY } =
      cardroomImageCrop;
    const coverScale = Math.max(size / naturalWidth, size / naturalHeight);
    const scale = coverScale * zoom;
    const drawWidth = naturalWidth * scale;
    const drawHeight = naturalHeight * scale;
    const maxOffsetX = Math.max(0, (drawWidth - size) / 2);
    const maxOffsetY = Math.max(0, (drawHeight - size) / 2);
    const drawX = (size - drawWidth) / 2 + (offsetX / 100) * maxOffsetX;
    const drawY = (size - drawHeight) / 2 + (offsetY / 100) * maxOffsetY;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);

    if (!returnDataUrl) return null;
    return canvas.toDataURL("image/jpeg", 0.86);
  };

  useEffect(() => {
    drawCardroomImageCrop();
  }, [cardroomImageCrop]);

  const drawAnnouncementImageCrop = (returnDataUrl = false) => {
    const canvas = announcementImageCanvasRef.current;
    if (!canvas || !announcementImageCrop?.image) return null;

    const width = ANNOUNCEMENT_IMAGE_OUTPUT_WIDTH;
    const height = ANNOUNCEMENT_IMAGE_OUTPUT_HEIGHT;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#111827";
    ctx.fillRect(0, 0, width, height);

    const { image, naturalWidth, naturalHeight, zoom, offsetX, offsetY } =
      announcementImageCrop;
    const coverScale = Math.max(width / naturalWidth, height / naturalHeight);
    const scale = coverScale * zoom;
    const drawWidth = naturalWidth * scale;
    const drawHeight = naturalHeight * scale;
    const maxOffsetX = Math.max(0, (drawWidth - width) / 2);
    const maxOffsetY = Math.max(0, (drawHeight - height) / 2);
    const drawX = (width - drawWidth) / 2 + (offsetX / 100) * maxOffsetX;
    const drawY = (height - drawHeight) / 2 + (offsetY / 100) * maxOffsetY;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);

    if (!returnDataUrl) return null;
    return canvas.toDataURL("image/jpeg", 0.86);
  };

  useEffect(() => {
    drawAnnouncementImageCrop();
  }, [announcementImageCrop]);

  const handleCardroomImageFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setCardroomImageError("Choose an image file.");
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      setCardroomImageError("Choose an image smaller than 8 MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        setCardroomImageCrop({
          fileName: file.name,
          image,
          src: reader.result,
          naturalWidth: image.naturalWidth,
          naturalHeight: image.naturalHeight,
          zoom: 1,
          offsetX: 0,
          offsetY: 0,
        });
        setCardroomImageError("");
      };
      image.onerror = () => {
        setCardroomImageError("Could not read that image.");
      };
      image.src = reader.result;
    };
    reader.onerror = () => {
      setCardroomImageError("Could not read that image.");
    };
    reader.readAsDataURL(file);
  };

  const handleApplyCardroomImageCrop = () => {
    const imageUrl = drawCardroomImageCrop(true);
    if (!imageUrl) {
      setCardroomImageError("Choose an image before applying the crop.");
      return;
    }
    setCardroomData((current) => ({ ...current, imageUrl }));
    setCardroomImageCrop(null);
    setCardroomImageError("");
  };

  const handleRemoveCardroomImage = () => {
    setCardroomImageCrop(null);
    setCardroomImageError("");
    setCardroomData((current) => ({ ...current, imageUrl: null }));
  };

  const handleAnnouncementImageFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setAnnouncementImageError("Choose an image file.");
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      setAnnouncementImageError("Choose an image smaller than 8 MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        setAnnouncementImageCrop({
          fileName: file.name,
          image,
          src: reader.result,
          naturalWidth: image.naturalWidth,
          naturalHeight: image.naturalHeight,
          zoom: 1,
          offsetX: 0,
          offsetY: 0,
        });
        setAnnouncementImageError("");
      };
      image.onerror = () => {
        setAnnouncementImageError("Could not read that image.");
      };
      image.src = reader.result;
    };
    reader.onerror = () => {
      setAnnouncementImageError("Could not read that image.");
    };
    reader.readAsDataURL(file);
  };

  const handleApplyAnnouncementImageCrop = () => {
    const imageUrl = drawAnnouncementImageCrop(true);
    if (!imageUrl) {
      setAnnouncementImageError("Choose an image before applying the crop.");
      return;
    }
    setCardroomData((current) => ({ ...current, announcementImageUrl: imageUrl }));
    setAnnouncementImageCrop(null);
    setAnnouncementImageError("");
  };

  const handleRemoveAnnouncementImage = () => {
    setAnnouncementImageCrop(null);
    setAnnouncementImageError("");
    setCardroomData((current) => ({ ...current, announcementImageUrl: null }));
  };

  const formatMembershipDate = (value) => {
    if (!value) return "Not available";
    return new Date(value).toLocaleDateString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatMembershipTier = (value) => {
    const tier = String(value || "").toLowerCase();
    if (tier === "basic") return "Basic";
    if (tier === "standard") return "Standard";
    if (tier === "pro") return "Pro";
    return "Not set";
  };

  const openMembershipPortal = async () => {
    setMembershipPortalError("");
    setMembershipPortalLoading(true);
    try {
      const { data } = await axios.post(
        `/${cardroom}/api/stripe/customer-portal`,
      );
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setMembershipPortalError(
        err.response?.data?.error || "Failed to open Stripe portal",
      );
    } finally {
      setMembershipPortalLoading(false);
    }
  };

  const openReviewsModal = async () => {
    setShowSettingsModal(false);
    setShowReviewsModal(true);
    setReviewsError("");
    setReviewsLoading(true);
    try {
      const { data } = await axios.get(`/${cardroom}/api/cardroom/reviews`);
      setReviewsData(data);
    } catch (err) {
      setReviewsError(
        err.response?.data?.error || "Failed to load review summary",
      );
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleToggleReviewPublic = async (review) => {
    const nextPublic = !review.public;
    try {
      await axios.patch(
        `/${cardroom}/api/cardroom/reviews/${review.id}/public`,
        { public: nextPublic ? 1 : 0 },
      );
      setReviewsData((current) => ({
        ...current,
        reviews: current.reviews.map((item) =>
          item.id === review.id ? { ...item, public: nextPublic } : item,
        ),
      }));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update review visibility");
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data } = await axios.get(`/${cardroom}/api/admin/users`);
      setUsers(data.users || []);
      setInvitations(data.invitations || []);
      setAccessErrors({});
    } catch (err) {
      console.error("Failed to fetch users:", err);
    } finally {
      setLoadingUsers(false);
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    setLoadingMessages(true);
    try {
      const { data } = await axios.get(`/${cardroom}/api/admin/messages`);
      setSupportMessages(data || []);
    } catch (err) {
      console.error("Failed to fetch support messages:", err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const fetchPlayerInvites = async () => {
    try {
      const { data } = await axios.get(`/${cardroom}/api/players/invites`);
      setPlayerInvites(data || []);
    } catch (err) {
      console.error("Failed to fetch player invites:", err);
    }
  };

  const openInviteModal = (playerId, playerName, action) => {
    setInviteModalPlayerId(playerId);
    setInviteModalPlayerName(playerName);
    setInviteModalAction(action);
    const defaultMsg =
      action === "approved"
        ? cardroomData?.defaultAcceptMessage || ""
        : cardroomData?.defaultRejectMessage || "";
    setInviteModalMessage(defaultMsg);
    setShowInviteModal(true);
  };

  const handleInviteSubmit = async () => {
    try {
      await axios.patch(
        `/${cardroom}/api/players/invites/${inviteModalPlayerId}`,
        {
          status: inviteModalAction,
          message: inviteModalMessage,
        },
      );
      setPlayerInvites((prev) =>
        prev.filter((inv) => inv.player_id !== inviteModalPlayerId),
      );
      setShowInviteModal(false);
    } catch (err) {
      alert("Failed to process invite");
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    setInviteError("");
    setInviteSuccess("");

    if (!isValidEmail(inviteEmail) || !inviteRole) {
      setInviteError("Enter a valid email and select a role.");
      return;
    }

    setInviteLoading(true);

    try {
      await axios.post(`/${cardroom}/api/admin/invite`, {
        email: inviteEmail,
        role: inviteRole,
      });
      setInviteSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      setInviteRole("");
      // Refresh the list to show new pending invitation
      fetchUsers();
    } catch (err) {
      setInviteError(err.response?.data?.error || "Failed to send invitation");
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvitation = async (inviteId) => {
    try {
      await axios.delete(`/${cardroom}/api/admin/invitations/${inviteId}`);
      setInvitations((prev) => prev.filter((inv) => inv.id !== inviteId));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to cancel invitation");
    }
  };

  const handleConfirmDeleteUser = async () => {
    if (!userDeleteTarget) return;
    setUserDeleteLoading(true);
    try {
      await axios.delete(`/${cardroom}/api/admin/users/${userDeleteTarget.id}`);
      setUsers((prev) =>
        prev.filter((user) => user.id !== userDeleteTarget.id),
      );
      setUserDeleteTarget(null);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete user");
    } finally {
      setUserDeleteLoading(false);
    }
  };

  const handleSubmitMessage = async (e) => {
    e.preventDefault();
    setMessageError("");
    setMessageSuccess("");

    try {
      await axios.post(`/${cardroom}/api/admin/messages`, { message });
      setMessageSuccess("Message submitted!");
      setMessage("");
      fetchMessages(); // Refresh message list
    } catch (err) {
      setMessageError(err.response?.data?.error || "Failed to submit message");
    }
  };

  const handleResolveMessage = async (msgId) => {
    try {
      await axios.delete(`/${cardroom}/api/admin/messages/${msgId}`);
      setSupportMessages((prev) => prev.filter((m) => m.id !== msgId));
    } catch (err) {
      console.error("Failed to resolve message:", err);
    }
  };

  const getUserAccess = (user) => {
    return {
      permissions: normalizePermissions(
        user.permissions || "",
        user.dealer_mode || 0,
      ),
      dealer_mode: Number(user.dealer_mode || 0),
    };
  };

  const hasPermission = (user, permissionKey) => {
    return getUserAccess(user).permissions.includes(permissionKey);
  };

  const saveAccess = async (user, nextAccess) => {
    const access = {
      permissions: normalizePermissions(
        nextAccess.permissions,
        nextAccess.dealer_mode,
      ),
      dealer_mode: Number(nextAccess.dealer_mode),
    };
    const previousAccess = getUserAccess(user);

    setUsers((prev) =>
      prev.map((existingUser) =>
        existingUser.id === user.id
          ? {
              ...existingUser,
              permissions: access.permissions,
              dealer_mode: access.dealer_mode,
            }
          : existingUser,
      ),
    );
    setSavingAccess((prev) => ({ ...prev, [user.id]: true }));
    setAccessErrors((prev) => ({ ...prev, [user.id]: "" }));

    try {
      const { data } = await axios.patch(
        `/${cardroom}/api/admin/users/${user.id}/access`,
        {
          permissions: access.permissions,
          dealer_mode: access.dealer_mode,
        },
      );

      setUsers((prev) =>
        prev.map((existingUser) =>
          existingUser.id === user.id
            ? {
                ...existingUser,
                permissions: data.permissions,
                dealer_mode: data.dealer_mode,
              }
            : existingUser,
        ),
      );
    } catch (err) {
      setUsers((prev) =>
        prev.map((existingUser) =>
          existingUser.id === user.id
            ? {
                ...existingUser,
                permissions: previousAccess.permissions,
                dealer_mode: previousAccess.dealer_mode,
              }
            : existingUser,
        ),
      );
      setAccessErrors((prev) => ({
        ...prev,
        [user.id]: err.response?.data?.error || "Failed to save access",
      }));
    } finally {
      setSavingAccess((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  const handleTogglePermission = (user, permissionKey) => {
    const access = getUserAccess(user);
    const permissionSet = new Set(access.permissions.split("").filter(Boolean));
    if (permissionSet.has(permissionKey)) {
      permissionSet.delete(permissionKey);
    } else {
      permissionSet.add(permissionKey);
    }
    saveAccess(user, {
      ...access,
      permissions: Array.from(permissionSet).join(""),
    });
  };

  const handleTournamentAccessToggle = (user, mode) => {
    const access = getUserAccess(user);
    const permissionSet = new Set(access.permissions.split("").filter(Boolean));

    if (mode === "enabled") {
      if (permissionSet.has(TOURNAMENT_ENABLED_KEY)) {
        permissionSet.delete(TOURNAMENT_ENABLED_KEY);
      } else {
        permissionSet.delete(TOURNAMENT_ONLY_KEY);
        permissionSet.add(TOURNAMENT_ENABLED_KEY);
      }
    }

    if (mode === "only") {
      if (permissionSet.has(TOURNAMENT_ONLY_KEY)) {
        permissionSet.delete(TOURNAMENT_ONLY_KEY);
      } else {
        permissionSet.clear();
        permissionSet.add(TOURNAMENT_ONLY_KEY);
      }
    }

    saveAccess(user, {
      ...access,
      permissions: Array.from(permissionSet).join(""),
    });
  };

  const handleDealerModeToggle = (user, dealerMode) => {
    const access = getUserAccess(user);
    const nextDealerMode = access.dealer_mode === dealerMode ? 0 : dealerMode;
    saveAccess(user, {
      permissions: access.permissions,
      dealer_mode: nextDealerMode,
    });
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    if (cardroomImageCrop) {
      alert("Apply or cancel the image crop before updating settings.");
      return;
    }
    if (announcementImageCrop) {
      alert("Apply or cancel the announcement image crop before updating settings.");
      return;
    }

    const mobileNotice = String(cardroomData.mobileNotice || "").trim();
    const announcementText = String(cardroomData.announcementText || "").trim();
    if (mobileNotice.length > 255) {
      alert("Mobile notice must be 255 characters or fewer.");
      return;
    }
    if (announcementText.length > 255) {
      alert("Announcement text must be 255 characters or fewer.");
      return;
    }
    const discoverText = String(cardroomData.discoverText || "").trim();
    if (discoverText.length > 255) {
      alert("Discover text must be 255 characters or fewer.");
      return;
    }

    const defaultCreditLimit =
      cardroomData.defaultCreditLimit === null ||
      cardroomData.defaultCreditLimit === undefined
        ? null
        : cardroomData.defaultCreditLimit === ""
          ? ""
          : Number(cardroomData.defaultCreditLimit);

    if (
      defaultCreditLimit !== null &&
      defaultCreditLimit !== "" &&
      (!Number.isInteger(defaultCreditLimit) || defaultCreditLimit < 0)
    ) {
      alert(
        "Default credit limit must be a non-negative whole number or turned off.",
      );
      return;
    }

    if (defaultCreditLimit === "") {
      alert("Enter a default credit limit amount, or turn the setting off.");
      return;
    }

    setSettingsLoading(true);
    try {
      await axios.patch(`/${cardroom}/api/cardroom/settings`, {
        default_max_players: cardroomData.default_max_players,
        waitlist_duration: cardroomData.waitlistDuration,
        default_credit_limit: defaultCreditLimit,
        tournament_default_tables: normalizeTournamentDefaultTables(
          cardroomData.tournamentDefaultTables,
        ),
        is_open: cardroomData.isOpen,
        default_accept_message: cardroomData.defaultAcceptMessage,
        default_reject_message: cardroomData.defaultRejectMessage,
        referral_bonus: cardroomData.referralBonus,
        referee_bonus: cardroomData.refereeBonus,
        first_time_bonus: cardroomData.firstTimeBonus,
        bad_beat: cardroomData.bad_beat,
        image_url: cardroomData.imageUrl || null,
        mobile_notice: mobileNotice || null,
        announcement_text: announcementText || null,
        announcement_image_url: cardroomData.announcementImageUrl || null,
        discoverable: cardroomData.discoverable ? 1 : 0,
        discover_address: cardroomData.discoverAddress || null,
        discover_city: cardroomData.discoverCity || null,
        discover_state: cardroomData.discoverState || null,
        discover_lat: cardroomData.discoverLat ?? null,
        discover_lng: cardroomData.discoverLng ?? null,
        discover_text: discoverText || null,
      });
      await fetchCardroom();
      setShowSettingsModal(false);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update settings");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleCloseSettingsModal = () => {
    if (originalCardroomData) {
      setCardroomData(originalCardroomData);
    }
    setCardroomImageCrop(null);
    setCardroomImageError("");
    setAnnouncementImageCrop(null);
    setAnnouncementImageError("");
    setDiscoverAddressQuery(originalCardroomData?.discoverAddress || "");
    setShowSettingsModal(false);
  };

  const generateCode = async () => {
    let code = "";
    let isUnique = false;
    setIsCheckingCode(true);

    // Try a few times to find a unique one randomly
    for (let i = 0; i < 5; i++) {
      code = Math.floor(1000 + Math.random() * 9000).toString();
      const { data } = await axios.get(
        `/api/cardroom/check-access-code/${code}`,
      );
      if (data.available) {
        isUnique = true;
        break;
      }
    }

    if (isUnique) {
      setNewAccessCode(code);
      setCodeAvailable(true);
    } else {
      alert(
        "Failed to generate a unique code. Please try again or enter one manually.",
      );
    }
    setIsCheckingCode(false);
  };

  const checkCodeManual = async (code) => {
    if (code.length !== 4) return;
    if (String(cardroomData?.accessCode || "") === code) {
      setCodeAvailable(true);
      return;
    }
    setIsCheckingCode(true);
    try {
      const { data } = await axios.get(
        `/api/cardroom/check-access-code/${code}`,
      );
      setCodeAvailable(data.available);
    } finally {
      setIsCheckingCode(false);
    }
  };

  const handleJoinNetwork = async () => {
    if (!newAccessCode || !codeAvailable || networkLoading) return;
    setNetworkLoading(true);
    try {
      await axios.post(`/${cardroom}/api/cardroom/join-network`, {
        access_code: newAccessCode,
        is_open: newIsOpen ? 1 : 0,
      });
      setShowNetworkModal(false);
      fetchCardroom();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to join network");
    } finally {
      setNetworkLoading(false);
    }
  };

  const thStyle = {
    padding: "0.75rem",
    textAlign: "left",
    color: "var(--game-text-secondary)",
    fontSize: "0.875rem",
  };

  const settingsUnchanged =
    cardroomData &&
    originalCardroomData &&
    cardroomData.default_max_players ===
      originalCardroomData.default_max_players &&
    cardroomData.waitlistDuration === originalCardroomData.waitlistDuration &&
    cardroomData.defaultCreditLimit ===
      originalCardroomData.defaultCreditLimit &&
    tournamentDefaultTablesEqual(
      cardroomData.tournamentDefaultTables,
      originalCardroomData.tournamentDefaultTables,
    ) &&
    cardroomData.isOpen === originalCardroomData.isOpen &&
    cardroomData.defaultAcceptMessage ===
      originalCardroomData.defaultAcceptMessage &&
    cardroomData.defaultRejectMessage ===
      originalCardroomData.defaultRejectMessage &&
    cardroomData.referralBonus === originalCardroomData.referralBonus &&
    cardroomData.refereeBonus === originalCardroomData.refereeBonus &&
    cardroomData.firstTimeBonus === originalCardroomData.firstTimeBonus &&
    cardroomData.bad_beat === originalCardroomData.bad_beat &&
    (cardroomData.imageUrl || "") === (originalCardroomData.imageUrl || "") &&
    (cardroomData.mobileNotice || "") ===
      (originalCardroomData.mobileNotice || "") &&
    (cardroomData.announcementText || "") ===
      (originalCardroomData.announcementText || "") &&
    (cardroomData.announcementImageUrl || "") ===
      (originalCardroomData.announcementImageUrl || "") &&
    Boolean(cardroomData.discoverable) ===
      Boolean(originalCardroomData.discoverable) &&
    (cardroomData.discoverAddress || "") ===
      (originalCardroomData.discoverAddress || "") &&
    (cardroomData.discoverCity || "") ===
      (originalCardroomData.discoverCity || "") &&
    (cardroomData.discoverState || "") ===
      (originalCardroomData.discoverState || "") &&
    String(cardroomData.discoverLat ?? "") ===
      String(originalCardroomData.discoverLat ?? "") &&
    String(cardroomData.discoverLng ?? "") ===
      String(originalCardroomData.discoverLng ?? "") &&
    (cardroomData.discoverText || "") ===
      (originalCardroomData.discoverText || "");
  const hasPendingImageCrop = Boolean(cardroomImageCrop || announcementImageCrop);
  const selectedInviteRole = INVITE_ROLE_DETAILS[inviteRole];
  const inviteFormValid = isValidEmail(inviteEmail) && Boolean(inviteRole);
  const hasMasterAccess = permission?.includes("M");
  const discoverEnabled = Boolean(cardroomData?.discoverable);
  const discoverLocationLock = getDiscoverLocationLock(
    originalCardroomData?.discoverLocationChangedAt,
  );
  const discoverLocationLocked = Boolean(
    discoverEnabled && discoverLocationLock.locked,
  );
  const discoverLocationChangedInForm = Boolean(
    cardroomData?.discoverAddress &&
      ((cardroomData.discoverAddress || "") !==
        (originalCardroomData?.discoverAddress || "") ||
        String(cardroomData.discoverLat ?? "") !==
          String(originalCardroomData?.discoverLat ?? "") ||
        String(cardroomData.discoverLng ?? "") !==
          String(originalCardroomData?.discoverLng ?? "")),
  );
  const discoverLocationInputDisabled = Boolean(
    discoverLocationLocked || discoverLocationChangedInForm,
  );
  const discoverLocationLockText =
    discoverLocationChangedInForm
      ? "Save settings before choosing another location."
      :
    discoverLocationLocked && discoverLocationLock.unlocksAt
      ? `Location can be changed again ${formatShortDateTime(
          discoverLocationLock.unlocksAt,
        )}.`
      : "";

  return (
    <div className="game">
      <BackOfficeHeader
        activePage="admin"
        cardroom={cardroom}
        centerTitle="Admin Console"
        dealerMode={dealerMode}
        title={`Welcome ${user}`}
      />
{/* MAIN CONTENT */}
      <div className="game-container">
        {loading ? (
          <div
            className="loading-container"
            style={{ padding: "4rem", textAlign: "center", color: "white" }}
          >
            <h2 style={{ marginBottom: "1.5rem", fontWeight: "700" }}>
              Gathering Data...
            </h2>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div
                className="spinner"
                style={{
                  border: "4px solid rgba(255,255,255,0.1)",
                  borderTop: "4px solid #6366f1",
                  borderRadius: "50%",
                  width: "50px",
                  height: "50px",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
            </div>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <>
            {/* Player Join Requests Section */}
            {playerInvites.length > 0 && (
              <section
                className="game-section"
                style={{ borderLeft: "4px solid #48bb78" }}
              >
                <header
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => setIsInvitesCollapsed(!isInvitesCollapsed)}
                >
                  <h2 className="game-section__title" style={{ margin: 0 }}>
                    Player Join Requests ({playerInvites.length})
                  </h2>
                  <div style={{ color: "var(--game-text-secondary)" }}>
                    {isInvitesCollapsed ? <FaChevronDown /> : <FaChevronUp />}
                  </div>
                </header>

                {!isInvitesCollapsed && (
                  <div
                    style={{
                      marginTop: "1.5rem",
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.75rem",
                    }}
                  >
                    {playerInvites.map((inv) => (
                      <div
                        key={inv.player_id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          backgroundColor: "rgba(255,255,255,0.02)",
                          padding: "0.75rem 1rem",
                          borderRadius: "8px",
                          border: "1px solid var(--game-border-light)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "1.5rem",
                            flex: 1,
                          }}
                        >
                          <span
                            style={{
                              color: "var(--game-text-primary)",
                              fontWeight: "bold",
                            }}
                          >
                            {inv.first_name} {inv.last_name}{" "}
                            {inv.nickname && `(${inv.nickname})`}
                          </span>
                          <span
                            style={{
                              color: "var(--game-text-secondary)",
                              fontSize: "0.9rem",
                            }}
                          >
                            {formatPhoneNumber(inv.phone_number)}
                          </span>
                          <span
                            style={{
                              color: "rgba(255, 255, 255, 0.3)",
                              fontSize: "0.8rem",
                            }}
                          >
                            Requested{" "}
                            {new Date(inv.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: "0.75rem" }}>
                          <button
                            className="game-btn game-btn--success"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.4rem",
                              fontSize: "0.75rem",
                              padding: "0.4rem 0.8rem",
                            }}
                            onClick={() =>
                              openInviteModal(
                                inv.player_id,
                                `${inv.first_name} ${inv.last_name}`,
                                "approved",
                              )
                            }
                          >
                            <FaUserCheck /> Approve
                          </button>
                          <button
                            className="game-btn game-btn--danger"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.4rem",
                              fontSize: "0.75rem",
                              padding: "0.4rem 0.8rem",
                            }}
                            onClick={() =>
                              openInviteModal(
                                inv.player_id,
                                `${inv.first_name} ${inv.last_name}`,
                                "rejected",
                              )
                            }
                          >
                            <FaUserTimes /> Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* Invite User Form */}
            {permission?.includes("U") && (
              <>
                <section className="game-section">
                  <h2 className="game-section__title">Invite New User</h2>
                  <form onSubmit={handleInviteUser} className="game-form">
                    <input
                      type="email"
                      className="game-input"
                      placeholder="Email address"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      required
                    />
                    <select
                      className="game-input"
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      required
                    >
                      <option value="" disabled>
                        Select Role
                      </option>
                      <option value="staff">Staff</option>
                      <option value="admin">Admin</option>
                      <option value="dealer">Dealer</option>
                      <option value="tournament">Tournament</option>
                    </select>
                    <button
                      type="submit"
                      className="game-btn game-btn--primary"
                      disabled={inviteLoading || !inviteFormValid}
                      style={{
                        opacity: inviteLoading || !inviteFormValid ? 0.55 : 1,
                      }}
                    >
                      {inviteLoading ? "Sending..." : "Send Invite"}
                    </button>
                  </form>
                  {selectedInviteRole && (
                    <div
                      style={{
                        marginTop: "1rem",
                        padding: "1rem",
                        border: "1px solid var(--game-border-light)",
                        borderRadius: "8px",
                        background: "rgba(255, 255, 255, 0.03)",
                      }}
                    >
                      <div
                        style={{
                          color: "var(--game-text-primary)",
                          fontWeight: 600,
                          marginBottom: "0.35rem",
                        }}
                      >
                        {selectedInviteRole.title} default access
                      </div>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.875rem",
                          lineHeight: 1.5,
                        }}
                      >
                        {selectedInviteRole.permissions} These defaults can be
                        changed after the user accepts the invite.
                      </p>
                    </div>
                  )}
                  {inviteError && (
                    <p
                      className="game-text-danger"
                      style={{ marginTop: "1rem" }}
                    >
                      {inviteError}
                    </p>
                  )}
                  {inviteSuccess && (
                    <p
                      className="game-text-success"
                      style={{ marginTop: "1rem" }}
                    >
                      {inviteSuccess}
                    </p>
                  )}
                </section>

                {/* Pending Invitations */}
                {invitations.length > 0 && (
                  <section className="game-section">
                    <h2 className="game-section__title">
                      Pending Invitations ({invitations.length})
                    </h2>
                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{ width: "100%", borderCollapse: "collapse" }}
                      >
                        <thead>
                          <tr
                            style={{
                              borderBottom: "1px solid var(--game-border)",
                            }}
                          >
                            <th style={thStyle}>Email</th>
                            <th style={thStyle}>Invited By</th>
                            <th style={thStyle}>Sent</th>
                            <th style={thStyle}>Expires</th>
                            <th style={thStyle}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invitations.map((inv) => (
                            <tr
                              key={inv.id}
                              style={{
                                borderBottom:
                                  "1px solid var(--game-border-light)",
                              }}
                            >
                              <td
                                style={{
                                  padding: "0.75rem",
                                  color: "var(--game-text-primary)",
                                }}
                              >
                                {inv.email}
                              </td>
                              <td
                                style={{
                                  padding: "0.75rem",
                                  color: "var(--game-text-primary)",
                                }}
                              >
                                {inv.invited_by_name}
                              </td>
                              <td
                                style={{
                                  padding: "0.75rem",
                                  color: "var(--game-text-secondary)",
                                  fontSize: "0.875rem",
                                }}
                              >
                                {new Date(inv.created_at).toLocaleString([], {
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </td>
                              <td
                                style={{
                                  padding: "0.75rem",
                                  color: "var(--game-text-secondary)",
                                  fontSize: "0.875rem",
                                }}
                              >
                                {new Date(inv.expires_at).toLocaleString([], {
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </td>
                              <td
                                style={{
                                  padding: "0.75rem",
                                  textAlign: "center",
                                }}
                              >
                                <button
                                  type="button"
                                  className="game-btn game-btn--danger"
                                  onClick={() => handleCancelInvitation(inv.id)}
                                  style={{
                                    padding: "0.4rem 0.75rem",
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  Cancel
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}
              </>
            )}

            {/* Settings Shortcut */}
            {cardroomData && (
              <section className="game-section">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "1rem",
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <h2
                      className="game-section__title"
                      style={{
                        margin: 0,
                        paddingBottom: 0,
                        borderBottom: "none",
                      }}
                    >
                      Cardroom Settings
                    </h2>
                    <p
                      className="game-text-secondary"
                      style={{ margin: "0.5rem 0 0", fontSize: "0.875rem" }}
                    >
                      Manage player defaults, join requests, bad beat, and
                      referral bonuses.
                    </p>
                  </div>
                  <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="game-btn game-btn--secondary"
                      onClick={() => setShowTutorialModal(true)}
                    >
                      View Tutorial
                    </button>
                    <button
                      type="button"
                      className="game-btn game-btn--secondary"
                      onClick={() => navigate(`/${cardroom}/guide`)}
                    >
                      Software Guide
                    </button>
                    <button
                      type="button"
                      className="game-btn game-btn--primary"
                      onClick={() => setShowSettingsModal(true)}
                      disabled={!permission?.includes("S")}
                    >
                      Manage Settings
                    </button>
                  </div>
                </div>
              </section>
            )}

            {permission?.includes("U") && (
              <section className="game-section">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "1rem",
                    flexWrap: "wrap",
                    marginBottom: "1rem",
                    paddingBottom: "0.75rem",
                    borderBottom: "1px solid var(--game-border)",
                  }}
                >
                  <div>
                    <h2
                      className="game-section__title"
                      style={{
                        margin: 0,
                        paddingBottom: 0,
                        borderBottom: "none",
                      }}
                    >
                      Users ({users.length})
                    </h2>
                    {usersView === "access" && (
                      <p
                        className="game-text-secondary"
                        style={{ margin: "0.35rem 0 0", fontSize: "0.875rem" }}
                      >
                        Backend permissions require Access Backend. Dealer Only
                        disables every other permission.
                      </p>
                    )}
                  </div>
                  <div
                    style={{
                      display: "inline-flex",
                      padding: "0.2rem",
                      border: "1px solid var(--game-border)",
                      borderRadius: "8px",
                      background: "var(--game-bg-tertiary)",
                    }}
                  >
                    {["access", "info"].map((view) => (
                      <button
                        key={view}
                        type="button"
                        onClick={() => setUsersView(view)}
                        style={{
                          border: 0,
                          borderRadius: "6px",
                          padding: "0.45rem 0.8rem",
                          cursor: "pointer",
                          color:
                            usersView === view
                              ? "white"
                              : "var(--game-text-secondary)",
                          background:
                            usersView === view
                              ? "var(--game-primary)"
                              : "transparent",
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      >
                        {view}
                      </button>
                    ))}
                  </div>
                </div>

                {loadingUsers ? (
                  <p className="game-text-secondary">Loading users...</p>
                ) : users.length === 0 ? (
                  <p className="game-text-secondary">No users found.</p>
                ) : usersView === "info" ? (
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <thead>
                        <tr
                          style={{
                            borderBottom: "1px solid var(--game-border)",
                          }}
                        >
                          <th style={{ ...thStyle, textAlign: "center" }}>
                            Username
                          </th>
                          <th style={{ ...thStyle, textAlign: "center" }}>
                            Email
                          </th>
                          <th style={{ ...thStyle, textAlign: "center" }}>
                            Last Login
                          </th>
                          <th style={{ ...thStyle, textAlign: "center" }}>
                            Invited By
                          </th>
                          <th style={{ ...thStyle, textAlign: "center" }}>
                            Created At
                          </th>
                          {hasMasterAccess && (
                            <th style={{ ...thStyle, textAlign: "center" }}>
                              Action
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => {
                          const isCurrentUser =
                            Number(user.id) === currentUserId;

                          return (
                            <tr
                              key={`info-${user.id}`}
                              style={{
                                borderBottom:
                                  "1px solid var(--game-border-light)",
                              }}
                            >
                              <td
                                style={{
                                  padding: "0.75rem",
                                  color: "var(--game-text-primary)",
                                }}
                              >
                                {user.username}
                              </td>
                              <td
                                style={{
                                  padding: "0.75rem",
                                  color: "var(--game-text-secondary)",
                                }}
                              >
                                {user.email || ""}
                              </td>
                              <td
                                style={{
                                  padding: "0.75rem",
                                  color: "var(--game-text-secondary)",
                                  fontSize: "0.875rem",
                                }}
                              >
                                {user.last_login
                                  ? new Date(user.last_login).toLocaleString(
                                      [],
                                      {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      },
                                    )
                                  : "Never"}
                              </td>
                              <td
                                style={{
                                  padding: "0.75rem",
                                  color: "var(--game-text-primary)",
                                }}
                              >
                                {user.created_by_name}
                              </td>
                              <td
                                style={{
                                  padding: "0.75rem",
                                  color: "var(--game-text-secondary)",
                                  fontSize: "0.875rem",
                                }}
                              >
                                {new Date(user.created_at).toLocaleString([], {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </td>
                              {hasMasterAccess && (
                                <td
                                  style={{
                                    padding: "0.75rem",
                                    textAlign: "center",
                                  }}
                                >
                                  {!isCurrentUser && (
                                    <button
                                      type="button"
                                      className="game-btn game-btn--danger"
                                      onClick={() => setUserDeleteTarget(user)}
                                      style={{
                                        padding: "0.4rem 0.75rem",
                                        fontSize: "0.8rem",
                                      }}
                                    >
                                      Delete
                                    </button>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        minWidth: "1180px",
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            borderBottom: "1px solid var(--game-border)",
                          }}
                        >
                          <th
                            style={{
                              ...thStyle,
                              minWidth: "160px",
                              textAlign: "center",
                              position: "sticky",
                              left: 0,
                              background: "var(--game-bg)",
                              zIndex: 1,
                            }}
                          >
                            User
                          </th>
                          {PERMISSION_GROUPS.map((group) => (
                            <th
                              key={group.label}
                              colSpan={group.permissions.length}
                              style={{
                                ...thStyle,
                                textAlign: "center",
                                borderLeft:
                                  "1px solid var(--game-border-light)",
                              }}
                            >
                              {group.label}
                            </th>
                          ))}
                          <th
                            colSpan={2}
                            style={{
                              ...thStyle,
                              textAlign: "center",
                              borderLeft: "1px solid var(--game-border-light)",
                            }}
                          >
                            Tournaments
                          </th>
                          <th
                            colSpan={2}
                            style={{
                              ...thStyle,
                              textAlign: "center",
                              borderLeft: "1px solid var(--game-border-light)",
                            }}
                          >
                            Dealer
                          </th>
                        </tr>
                        <tr
                          style={{
                            borderBottom: "1px solid var(--game-border)",
                          }}
                        >
                          <th
                            style={{
                              ...thStyle,
                              position: "sticky",
                              left: 0,
                              background: "var(--game-bg)",
                              zIndex: 1,
                            }}
                          />
                          {ALL_PERMISSIONS.map((permission) => (
                            <th
                              key={permission.key}
                              style={{
                                ...thStyle,
                                width: "92px",
                                textAlign: "center",
                                fontSize: "0.75rem",
                                lineHeight: 1.25,
                                whiteSpace: "normal",
                              }}
                            >
                              {permission.label}
                            </th>
                          ))}
                          <th
                            style={{
                              ...thStyle,
                              width: "92px",
                              textAlign: "center",
                              fontSize: "0.75rem",
                              lineHeight: 1.25,
                              borderLeft: "1px solid var(--game-border-light)",
                            }}
                          >
                            Tournament Enabled
                          </th>
                          <th
                            style={{
                              ...thStyle,
                              width: "92px",
                              textAlign: "center",
                              fontSize: "0.75rem",
                              lineHeight: 1.25,
                            }}
                          >
                            Tournament Only
                          </th>
                          <th
                            style={{
                              ...thStyle,
                              width: "92px",
                              textAlign: "center",
                              fontSize: "0.75rem",
                              lineHeight: 1.25,
                            }}
                          >
                            Dealer Enabled
                          </th>
                          <th
                            style={{
                              ...thStyle,
                              width: "92px",
                              textAlign: "center",
                              fontSize: "0.75rem",
                              lineHeight: 1.25,
                            }}
                          >
                            Dealer Only
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => {
                          const access = getUserAccess(user);
                          const dealerOnly = Number(access.dealer_mode) === 2;
                          const tournamentOnly =
                            access.permissions.includes(TOURNAMENT_ONLY_KEY);
                          const hasBackendAccess =
                            access.permissions.includes("V");
                          const isCurrentUser =
                            Number(user.id) === currentUserId;

                          return (
                            <tr
                              key={`access-${user.id}`}
                              style={{
                                borderBottom:
                                  "1px solid var(--game-border-light)",
                              }}
                            >
                              <td
                                style={{
                                  padding: "0.75rem",
                                  color: "var(--game-text-primary)",
                                  position: "sticky",
                                  left: 0,
                                  background: "var(--game-bg)",
                                  zIndex: 1,
                                }}
                              >
                                <div style={{ fontWeight: 600 }}>
                                  {user.name}
                                </div>
                                {accessErrors[user.id] && (
                                  <div
                                    style={{
                                      color: "#f56565",
                                      fontSize: "0.75rem",
                                      marginTop: "0.35rem",
                                    }}
                                  >
                                    {accessErrors[user.id]}
                                  </div>
                                )}
                              </td>
                              {ALL_PERMISSIONS.map((permission) => {
                                const disabled =
                                  isCurrentUser ||
                                  dealerOnly ||
                                  tournamentOnly ||
                                  (permission.requiresBackend &&
                                    !hasBackendAccess);
                                return (
                                  <td
                                    key={`${user.id}-${permission.key}`}
                                    style={{
                                      padding: "0.75rem",
                                      textAlign: "center",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={hasPermission(
                                        user,
                                        permission.key,
                                      )}
                                      disabled={
                                        disabled || savingAccess[user.id]
                                      }
                                      title={
                                        isCurrentUser
                                          ? "You cannot change your own access"
                                          : tournamentOnly
                                            ? "Tournament Only disables other access"
                                          : disabled
                                            ? "Enable the required parent setting first"
                                            : permission.label
                                      }
                                      onChange={() =>
                                        handleTogglePermission(
                                          user,
                                          permission.key,
                                        )
                                      }
                                      style={{
                                        width: "18px",
                                        height: "18px",
                                        cursor: disabled
                                          ? "not-allowed"
                                          : "pointer",
                                        opacity: disabled ? 0.45 : 1,
                                      }}
                                    />
                                  </td>
                                );
                              })}
                              <td
                                style={{
                                  padding: "0.75rem",
                                  textAlign: "center",
                                  borderLeft:
                                    "1px solid var(--game-border-light)",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={access.permissions.includes(
                                    TOURNAMENT_ENABLED_KEY,
                                  )}
                                  disabled={
                                    isCurrentUser ||
                                    dealerOnly ||
                                    tournamentOnly ||
                                    savingAccess[user.id]
                                  }
                                  title={
                                    isCurrentUser
                                      ? "You cannot change your own access"
                                      : tournamentOnly
                                        ? "Tournament Only is selected"
                                        : "Tournament Enabled"
                                  }
                                  onChange={() =>
                                    handleTournamentAccessToggle(user, "enabled")
                                  }
                                  style={{
                                    width: "18px",
                                    height: "18px",
                                    cursor:
                                      isCurrentUser ||
                                      dealerOnly ||
                                      tournamentOnly ||
                                      savingAccess[user.id]
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity:
                                      isCurrentUser ||
                                      dealerOnly ||
                                      tournamentOnly ||
                                      savingAccess[user.id]
                                        ? 0.45
                                        : 1,
                                  }}
                                />
                              </td>
                              <td
                                style={{
                                  padding: "0.75rem",
                                  textAlign: "center",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={tournamentOnly}
                                  disabled={
                                    isCurrentUser ||
                                    dealerOnly ||
                                    savingAccess[user.id]
                                  }
                                  title={
                                    isCurrentUser
                                      ? "You cannot change your own access"
                                      : dealerOnly
                                        ? "Dealer Only disables tournament access"
                                        : "Tournament Only"
                                  }
                                  onChange={() =>
                                    handleTournamentAccessToggle(user, "only")
                                  }
                                  style={{
                                    width: "18px",
                                    height: "18px",
                                    cursor:
                                      isCurrentUser ||
                                      dealerOnly ||
                                      savingAccess[user.id]
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity:
                                      isCurrentUser ||
                                      dealerOnly ||
                                      savingAccess[user.id]
                                        ? 0.45
                                        : 1,
                                  }}
                                />
                              </td>
                              <td
                                style={{
                                  padding: "0.75rem",
                                  textAlign: "center",
                                  borderLeft:
                                    "1px solid var(--game-border-light)",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={Number(access.dealer_mode) === 1}
                                  disabled={
                                    isCurrentUser || savingAccess[user.id]
                                  }
                                  title={
                                    isCurrentUser
                                      ? "You cannot change your own access"
                                      : "Dealer Enabled"
                                  }
                                  onChange={() =>
                                    handleDealerModeToggle(user, 1)
                                  }
                                  style={{
                                    width: "18px",
                                    height: "18px",
                                    cursor:
                                      isCurrentUser || savingAccess[user.id]
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity:
                                      isCurrentUser || savingAccess[user.id]
                                        ? 0.45
                                        : 1,
                                  }}
                                />
                              </td>
                              <td
                                style={{
                                  padding: "0.75rem",
                                  textAlign: "center",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={Number(access.dealer_mode) === 2}
                                  disabled={
                                    isCurrentUser || savingAccess[user.id]
                                  }
                                  title={
                                    isCurrentUser
                                      ? "You cannot change your own access"
                                      : "Dealer Only"
                                  }
                                  onChange={() =>
                                    handleDealerModeToggle(user, 2)
                                  }
                                  style={{
                                    width: "18px",
                                    height: "18px",
                                    cursor:
                                      isCurrentUser || savingAccess[user.id]
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity:
                                      isCurrentUser || savingAccess[user.id]
                                        ? 0.45
                                        : 1,
                                  }}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}

            {/* Submit Message Form */}
            <section className="game-section">
              <h2 className="game-section__title">Contact Support</h2>
              <form
                onSubmit={handleSubmitMessage}
                className="game-form-vertical"
              >
                <textarea
                  className="game-input"
                  placeholder="Enter your message or comment"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={4}
                  style={{ resize: "vertical", fontFamily: "inherit" }}
                />
                <div style={{ display: "flex", justifyContent: "flex-start" }}>
                  <button type="submit" className="game-btn game-btn--primary">
                    Submit Message
                  </button>
                </div>
              </form>
              {messageError && (
                <p className="game-text-danger" style={{ marginTop: "1rem" }}>
                  {messageError}
                </p>
              )}
              {messageSuccess && (
                <p className="game-text-success" style={{ marginTop: "1rem" }}>
                  {messageSuccess}
                </p>
              )}

              {/* Support Tickets Section */}
              {supportMessages.length > 0 && (
                <div style={{ marginTop: "2rem" }}>
                  <h3
                    style={{
                      fontSize: "1rem",
                      color: "var(--game-text-primary)",
                      marginBottom: "1rem",
                    }}
                  >
                    Your Support Tickets
                  </h3>
                  {loadingMessages ? (
                    <p className="game-text-secondary">Loading tickets...</p>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                      }}
                    >
                      {supportMessages.map((msg) => (
                        <div
                          key={msg.id}
                          style={{
                            background: "rgba(255, 255, 255, 0.03)",
                            border: "1px solid var(--game-border-light)",
                            borderRadius: "8px",
                            padding: "1rem",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: "0.5rem",
                            }}
                          >
                            <p
                              style={{
                                color: "var(--game-text-primary)",
                                fontSize: "0.9375rem",
                                margin: 0,
                                lineHeight: 1.5,
                              }}
                            >
                              {msg.message}
                            </p>
                            <span
                              style={{
                                fontSize: "0.75rem",
                                color: "var(--game-text-secondary)",
                                whiteSpace: "nowrap",
                                marginLeft: "1rem",
                              }}
                            >
                              {new Date(msg.created_at).toLocaleString([], {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>

                          {msg.response ? (
                            <div
                              style={{
                                marginTop: "1rem",
                                padding: "1rem",
                                background: "rgba(99, 102, 241, 0.1)",
                                borderRadius: "6px",
                                borderLeft: "3px solid #6366f1",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "0.8125rem",
                                  color: "#818cf8",
                                  fontWeight: 600,
                                  marginBottom: "0.4rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                }}
                              >
                                Support Response:
                              </p>
                              <p
                                style={{
                                  fontSize: "0.9rem",
                                  color: "var(--game-text-primary)",
                                  margin: 0,
                                  lineHeight: 1.5,
                                }}
                              >
                                {msg.response}
                              </p>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  marginTop: "0.75rem",
                                }}
                              >
                                <button
                                  onClick={() => handleResolveMessage(msg.id)}
                                  className="game-btn game-btn--success"
                                  style={{
                                    padding: "0.4rem 0.8rem",
                                    fontSize: "0.75rem",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.4rem",
                                  }}
                                >
                                  <FaCheckCircle />
                                  Resolve & Close
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div
                              style={{
                                marginTop: "0.75rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.4rem",
                                  color: "#d69e2e",
                                  fontSize: "0.8125rem",
                                }}
                              >
                                <FaClock style={{ fontSize: "0.75rem" }} />
                                Pending Support Review
                              </div>

                              <div style={{ marginLeft: "auto" }}>
                                <button
                                  onClick={() => handleResolveMessage(msg.id)}
                                  className="game-btn game-btn--danger"
                                  style={{
                                    padding: "0.4rem 0.8rem",
                                    fontSize: "0.75rem",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.4rem",
                                  }}
                                >
                                  <FaTimesCircle />
                                  Delete
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </div>
      {/* Invite Action Modal */}
      {showInviteModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setShowInviteModal(false)}
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                {inviteModalAction === "approved" ? "Approve" : "Reject"} Player
              </h3>
            </div>
            <div className="game-modal__body">
              <p
                style={{
                  color: "var(--game-text-secondary)",
                  fontSize: "0.875rem",
                  marginBottom: "1.25rem",
                }}
              >
                {inviteModalAction === "approved" ? (
                  <>
                    You are approving{" "}
                    <strong style={{ color: "var(--game-text-primary)" }}>
                      {inviteModalPlayerName}
                    </strong>
                    . This message will be sent to the player.
                  </>
                ) : (
                  <>
                    You are rejecting{" "}
                    <strong style={{ color: "var(--game-text-primary)" }}>
                      {inviteModalPlayerName}
                    </strong>
                    . This message will be sent to the player.
                  </>
                )}
              </p>
              <div className="game-form-vertical">
                <label className="game-label">Message</label>
                <textarea
                  className="game-input"
                  value={inviteModalMessage}
                  onChange={(e) => setInviteModalMessage(e.target.value)}
                  rows={4}
                  style={{
                    resize: "vertical",
                    fontFamily: "inherit",
                    fontSize: "0.875rem",
                  }}
                />
              </div>
              <div
                className="game-modal__footer"
                style={{ marginTop: "1.5rem" }}
              >
                <button
                  className="game-btn game-btn--ghost"
                  onClick={() => setShowInviteModal(false)}
                >
                  Cancel
                </button>
                <button
                  className={`game-btn ${inviteModalAction === "approved" ? "game-btn--success" : "game-btn--danger"}`}
                  onClick={handleInviteSubmit}
                >
                  {inviteModalAction === "approved"
                    ? "Approve & Send"
                    : "Reject & Send"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Delete User Modal */}
      {userDeleteTarget && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => !userDeleteLoading && setUserDeleteTarget(null)}
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title" style={{ color: "#f56565" }}>
                Delete User
              </h3>
              <button
                type="button"
                className="game-icon-btn"
                onClick={() => setUserDeleteTarget(null)}
                disabled={userDeleteLoading}
                aria-label="Close delete user warning"
              >
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body">
              <p
                style={{
                  color: "var(--game-text-primary)",
                  fontSize: "0.95rem",
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                Delete <strong>{userDeleteTarget.name}</strong> from this
                cardroom?
              </p>
              <p
                style={{
                  color: "var(--game-text-secondary)",
                  fontSize: "0.875rem",
                  margin: "0.75rem 0 0",
                  lineHeight: 1.5,
                }}
              >
                This removes their user account and cannot be undone.
              </p>
            </div>
            <div className="game-modal__footer">
              <button
                type="button"
                className="game-btn game-btn--ghost"
                onClick={() => setUserDeleteTarget(null)}
                disabled={userDeleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="game-btn game-btn--danger"
                onClick={handleConfirmDeleteUser}
                disabled={userDeleteLoading}
              >
                {userDeleteLoading ? "Deleting..." : "Delete User"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Membership Modal */}
      {showMembershipModal && cardroomData && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setShowMembershipModal(false)}
          />
          <div className="game-modal" style={{ maxWidth: "560px" }}>
            <div className="game-modal__header">
              <div>
                <h3 className="game-modal__title">Membership</h3>
                <p
                  className="game-text-secondary"
                  style={{ margin: "0.35rem 0 0", fontSize: "0.85rem" }}
                >
                  Review your current billing status and manage payment details.
                </p>
              </div>
              <button
                type="button"
                className="game-icon-btn"
                onClick={() => setShowMembershipModal(false)}
                aria-label="Close membership"
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: "0.85rem",
                }}
              >
                <div>
                  <p className="game-text-secondary" style={{ margin: 0 }}>
                    Tier
                  </p>
                  <strong>
                    {formatMembershipTier(cardroomData.membershipTierKey)}
                  </strong>
                </div>
                <div>
                  <p className="game-text-secondary" style={{ margin: 0 }}>
                    Status
                  </p>
                  <strong>{cardroomData.membershipStatus || "inactive"}</strong>
                </div>
                <div>
                  <p className="game-text-secondary" style={{ margin: 0 }}>
                    Billing
                  </p>
                  <strong>{cardroomData.billingCycle || "Not set"}</strong>
                </div>
                <div>
                  <p className="game-text-secondary" style={{ margin: 0 }}>
                    Renews / Expires
                  </p>
                  <strong>
                    {formatMembershipDate(cardroomData.membershipExpiresAt)}
                  </strong>
                </div>
              </div>

              {cardroomData.cancelAtPeriodEnd && (
                <p className="game-text-secondary">
                  This membership is scheduled to cancel at the end of the
                  current billing period.
                </p>
              )}

              {membershipPortalError && (
                <div style={{ color: "#f87171", fontWeight: 700 }}>
                  {membershipPortalError}
                </div>
              )}

              <p className="game-text-secondary">
                To update payment methods, view invoices, change plans, or
                cancel,{" "}
                <button
                  type="button"
                  onClick={openMembershipPortal}
                  disabled={membershipPortalLoading}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--game-primary)",
                    cursor: membershipPortalLoading ? "wait" : "pointer",
                    padding: 0,
                    textDecoration: "underline",
                    font: "inherit",
                  }}
                >
                  {membershipPortalLoading
                    ? "opening Stripe..."
                    : "open the Stripe customer portal"}
                </button>
                .
              </p>
            </div>
          </div>
        </>
      )}
      {/* Reviews Modal */}
      {showReviewsModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setShowReviewsModal(false)}
          />
          <div className="game-modal game-modal--large" style={{ maxWidth: "760px" }}>
            <div className="game-modal__header">
              <div>
                <h3 className="game-modal__title">Anonymous Reviews</h3>
                <p
                  className="game-text-secondary"
                  style={{ margin: "0.35rem 0 0", fontSize: "0.85rem" }}
                >
                  Ratings are shown as category averages. Written reviews do not
                  include player names.
                </p>
              </div>
              <button
                type="button"
                className="game-icon-btn"
                onClick={() => setShowReviewsModal(false)}
                aria-label="Close reviews"
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {reviewsLoading ? (
                <p className="game-text-secondary">Loading reviews...</p>
              ) : reviewsError ? (
                <p className="game-text-danger">{reviewsError}</p>
              ) : reviewsData ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                      gap: "0.75rem",
                    }}
                  >
                    {reviewsData.categories.map((category) => (
                      <div
                        key={category.key}
                        style={{
                          padding: "0.85rem",
                          border: "1px solid var(--game-border-light)",
                          borderRadius: "8px",
                          background: "rgba(255,255,255,0.03)",
                        }}
                      >
                        <div
                          className="game-text-secondary"
                          style={{ fontSize: "0.78rem", marginBottom: "0.35rem" }}
                        >
                          {category.label}
                        </div>
                        <strong style={{ color: "var(--game-text-primary)" }}>
                          {Number(category.average || 0).toFixed(1)}/5
                        </strong>
                      </div>
                    ))}
                  </div>

                  <div>
                    <h4 style={{ margin: "0 0 0.75rem", color: "var(--game-text-primary)" }}>
                      Written Reviews ({reviewsData.reviews.length})
                    </h4>
                    {reviewsData.reviews.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                        {reviewsData.reviews.map((review, index) => (
                          <div
                            key={review.id || `${review.updatedAt}-${index}`}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              gap: "1rem",
                              padding: "1rem",
                              border: review.public
                                ? "2px solid var(--game-success)"
                                : "2px solid var(--game-border)",
                              borderRadius: "8px",
                              background: "rgba(255,255,255,0.035)",
                            }}
                          >
                            <p style={{ margin: 0, lineHeight: 1.5, flex: 1 }}>
                              {review.text}
                            </p>
                            <button
                              type="button"
                              className={
                                review.public
                                  ? "game-btn game-btn--danger"
                                  : "game-icon-btn game-icon-btn--success"
                              }
                              onClick={() => handleToggleReviewPublic(review)}
                              style={{
                                padding: review.public ? "0.4rem 0.75rem" : "0.5rem 0.75rem",
                                fontSize: "0.78rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {review.public ? "Hide" : "Show"}
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="game-text-secondary">No written reviews yet.</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="game-text-secondary">No review data loaded.</p>
              )}
            </div>
          </div>
        </>
      )}
      {/* Settings Modal */}
      {showSettingsModal && cardroomData && (
        <>
          <div
            className="game-modal-overlay"
            onClick={handleCloseSettingsModal}
          />
          <div
            className="game-modal game-modal--large"
            style={{ maxWidth: "820px" }}
          >
            <div className="game-modal__header">
              <div>
                <h3 className="game-modal__title">Cardroom Settings</h3>
              </div>
              <button
                type="button"
                className="game-icon-btn"
                onClick={handleCloseSettingsModal}
                aria-label="Close settings"
              >
                <FaTimes />
              </button>
            </div>

            <form
              onSubmit={handleUpdateSettings}
              style={{ display: "flex", flexDirection: "column", minHeight: 0 }}
            >
              <nav className="settings-section-nav" aria-label="Settings sections">
                {SETTINGS_MODAL_SECTIONS.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    className={`settings-section-nav__button settings-section-nav__button--${section.id.replace(
                      "settings-",
                      "",
                    )}`}
                    onClick={() =>
                      document.getElementById(section.id)?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      })
                    }
                  >
                    <span className="settings-section-nav__label">
                      {section.label}
                    </span>
                    <span className="settings-section-nav__description">
                      {section.description}
                    </span>
                  </button>
                ))}
              </nav>

              <div className="game-modal__body game-modal__body--scroll">
                <div className="settings-section-list">
                  {SETTINGS_MODAL_SECTIONS.map((section) => (
                    <div
                      key={section.id}
                      id={section.id}
                      className={`settings-section-heading settings-section-heading--${section.id.replace(
                        "settings-",
                        "",
                      )}`}
                    >
                      <div>
                        <h4>{section.label}</h4>
                        <p>{section.description}</p>
                      </div>
                    </div>
                  ))}

                  <div
                    className="settings-field settings-field--general"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "start",
                      padding: "0.9rem 0",
                      borderBottom: "1px solid var(--game-border-light)",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Club Image
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        Square image shown on the player mobile club screen.
                      </p>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "minmax(120px, 160px) minmax(180px, 1fr)",
                        gap: "1rem",
                        alignItems: "start",
                      }}
                    >
                      <div>
                        {cardroomImageCrop ? (
                          <canvas
                            ref={cardroomImageCanvasRef}
                            aria-label="Club image crop preview"
                            style={{
                              width: "100%",
                              aspectRatio: "1 / 1",
                              borderRadius: "10px",
                              border: "1px solid var(--game-border-light)",
                              background: "rgba(255,255,255,0.04)",
                              display: "block",
                            }}
                          />
                        ) : cardroomData.imageUrl ? (
                          <img
                            src={cardroomData.imageUrl}
                            alt=""
                            style={{
                              width: "100%",
                              aspectRatio: "1 / 1",
                              objectFit: "cover",
                              borderRadius: "10px",
                              border: "1px solid var(--game-border-light)",
                              background: "rgba(255,255,255,0.04)",
                              display: "block",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              aspectRatio: "1 / 1",
                              borderRadius: "10px",
                              border: "1px dashed var(--game-border-light)",
                              background: "rgba(255,255,255,0.04)",
                              color: "var(--game-text-secondary)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.8rem",
                              textAlign: "center",
                              padding: "0.75rem",
                              boxSizing: "border-box",
                            }}
                          >
                            No image
                          </div>
                        )}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.75rem",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "0.6rem",
                            flexWrap: "wrap",
                          }}
                        >
                          <label
                            className="game-btn game-btn--secondary"
                            style={{
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            Choose Image
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleCardroomImageFile}
                              style={{ display: "none" }}
                            />
                          </label>
                          {(cardroomData.imageUrl || cardroomImageCrop) && (
                            <button
                              type="button"
                              className="game-btn game-btn--ghost"
                              onClick={handleRemoveCardroomImage}
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        {cardroomImageCrop && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "0.65rem",
                            }}
                          >
                            <span
                              className="game-text-secondary"
                              style={{ fontSize: "0.78rem" }}
                            >
                              Cropping {cardroomImageCrop.fileName}
                            </span>
                            <label className="game-label">
                              Zoom
                              <input
                                type="range"
                                min="1"
                                max="3"
                                step="0.01"
                                value={cardroomImageCrop.zoom}
                                onChange={(e) =>
                                  setCardroomImageCrop((current) => ({
                                    ...current,
                                    zoom: Number(e.target.value),
                                  }))
                                }
                                style={{ width: "100%" }}
                              />
                            </label>
                            <label className="game-label">
                              Horizontal
                              <input
                                type="range"
                                min="-100"
                                max="100"
                                step="1"
                                value={cardroomImageCrop.offsetX}
                                onChange={(e) =>
                                  setCardroomImageCrop((current) => ({
                                    ...current,
                                    offsetX: Number(e.target.value),
                                  }))
                                }
                                style={{ width: "100%" }}
                              />
                            </label>
                            <label className="game-label">
                              Vertical
                              <input
                                type="range"
                                min="-100"
                                max="100"
                                step="1"
                                value={cardroomImageCrop.offsetY}
                                onChange={(e) =>
                                  setCardroomImageCrop((current) => ({
                                    ...current,
                                    offsetY: Number(e.target.value),
                                  }))
                                }
                                style={{ width: "100%" }}
                              />
                            </label>
                            <div
                              style={{
                                display: "flex",
                                gap: "0.6rem",
                                flexWrap: "wrap",
                              }}
                            >
                              <button
                                type="button"
                                className="game-btn game-btn--primary"
                                onClick={handleApplyCardroomImageCrop}
                              >
                                Apply Crop
                              </button>
                              <button
                                type="button"
                                className="game-btn game-btn--ghost"
                                onClick={() => {
                                  setCardroomImageCrop(null);
                                  setCardroomImageError("");
                                }}
                              >
                                Cancel Crop
                              </button>
                            </div>
                          </div>
                        )}

                        {cardroomImageError && (
                          <p
                            className="game-text-danger"
                            style={{ margin: 0, fontSize: "0.8rem" }}
                          >
                            {cardroomImageError}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div
                    className="settings-field settings-field--tournaments"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "start",
                      padding: "0.9rem 0",
                      borderBottom: "1px solid var(--game-border-light)",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Tournament Default Tables
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        Tables automatically created when a tournament starts.
                      </p>
                    </div>
                    <div style={{ display: "grid", gap: "0.75rem" }}>
                      {(cardroomData.tournamentDefaultTables || ["Table 1"]).map(
                        (tableName, index) => (
                          <div
                            key={index}
                            style={{
                              display: "grid",
                              gridTemplateColumns: "minmax(0, 1fr) auto",
                              gap: "0.5rem",
                              alignItems: "center",
                            }}
                          >
                            <input
                              className="game-input"
                              value={tableName}
                              onChange={(e) => {
                                const nextTables = [
                                  ...(cardroomData.tournamentDefaultTables ||
                                    ["Table 1"]),
                                ];
                                nextTables[index] = e.target.value;
                                setCardroomData({
                                  ...cardroomData,
                                  tournamentDefaultTables: nextTables.slice(0, 4),
                                });
                              }}
                              placeholder={`Table ${index + 1}`}
                              style={{ width: "100%", boxSizing: "border-box" }}
                            />
                            <button
                              type="button"
                              className="game-icon-btn game-icon-btn--danger"
                              title="Remove table"
                              disabled={
                                (cardroomData.tournamentDefaultTables || [])
                                  .length <= 1
                              }
                              onClick={() => {
                                const nextTables = [
                                  ...(cardroomData.tournamentDefaultTables ||
                                    ["Table 1"]),
                                ].filter((_, tableIndex) => tableIndex !== index);
                                setCardroomData({
                                  ...cardroomData,
                                  tournamentDefaultTables:
                                    nextTables.length > 0 ? nextTables : ["Table 1"],
                                });
                              }}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        ),
                      )}
                      {(cardroomData.tournamentDefaultTables || []).length < 4 && (
                                              <button
                        type="button"
                        className="game-icon-btn game-icon-btn--info"
                        disabled={
                          (cardroomData.tournamentDefaultTables || []).length >= 4
                        }
                        onClick={() => {
                          const currentTables =
                            cardroomData.tournamentDefaultTables || ["Table 1"];
                          if (currentTables.length >= 4) return;
                          setCardroomData({
                            ...cardroomData,
                            tournamentDefaultTables: [
                              ...currentTables,
                              `Table ${currentTables.length + 1}`,
                            ],
                          });
                        }}
                        style={{
                          width: "100%",
                          justifyContent: "center",
                          gap: "0.5rem",
                          padding: "0.75rem",
                        }}
                      >
                        <FaPlus /> Add Table
                      </button>
                      )}

                    </div>
                  </div>

                  <div
                    className="settings-field settings-field--nexus-mobile"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "start",
                      padding: "0.9rem 0",
                      borderBottom: "1px solid var(--game-border-light)",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Mobile Notice
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        Short note shown in the club screen notice row.
                      </p>
                    </div>
                    <div>
                      <textarea
                        className="game-input"
                        value={cardroomData.mobileNotice || ""}
                        onChange={(e) =>
                          setCardroomData({
                            ...cardroomData,
                            mobileNotice: e.target.value.slice(0, 255),
                          })
                        }
                        rows={3}
                        maxLength={255}
                        placeholder="Example: New members must check in with staff before joining a table."
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          resize: "vertical",
                          fontFamily: "inherit",
                          fontSize: "0.85rem",
                        }}
                      />
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: "0.35rem 0 0",
                          fontSize: "0.75rem",
                          textAlign: "right",
                        }}
                      >
                        {(cardroomData.mobileNotice || "").length}/255
                      </p>
                    </div>
                  </div>

                  <div
                    className="settings-field settings-field--nexus-mobile"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "start",
                      padding: "0.9rem 0",
                      borderBottom: "1px solid var(--game-border-light)",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Mobile Announcement
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        Bottom-sheet popup for mobile players. Image-only is
                        recommended.
                      </p>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.85rem",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "minmax(160px, 240px) minmax(180px, 1fr)",
                          gap: "1rem",
                          alignItems: "start",
                        }}
                      >
                        <div>
                          {announcementImageCrop ? (
                            <canvas
                              ref={announcementImageCanvasRef}
                              aria-label="Announcement image crop preview"
                              style={{
                                width: "100%",
                                aspectRatio: "900 / 620",
                                borderRadius: "10px",
                                border: "1px solid var(--game-border-light)",
                                background: "rgba(255,255,255,0.04)",
                                display: "block",
                              }}
                            />
                          ) : cardroomData.announcementImageUrl ? (
                            <img
                              src={cardroomData.announcementImageUrl}
                              alt=""
                              style={{
                                width: "100%",
                                aspectRatio: "900 / 620",
                                objectFit: "cover",
                                borderRadius: "10px",
                                border: "1px solid var(--game-border-light)",
                                background: "rgba(255,255,255,0.04)",
                                display: "block",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                aspectRatio: "900 / 620",
                                borderRadius: "10px",
                                border: "1px dashed var(--game-border-light)",
                                background: "rgba(255,255,255,0.04)",
                                color: "var(--game-text-secondary)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.8rem",
                                textAlign: "center",
                                padding: "0.75rem",
                                boxSizing: "border-box",
                              }}
                            >
                              No image
                            </div>
                          )}
                        </div>

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.75rem",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "0.6rem",
                              flexWrap: "wrap",
                            }}
                          >
                            <label
                              className="game-btn game-btn--secondary"
                              style={{
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Choose Image
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleAnnouncementImageFile}
                                style={{ display: "none" }}
                              />
                            </label>
                            {(cardroomData.announcementImageUrl ||
                              announcementImageCrop) && (
                              <button
                                type="button"
                                className="game-btn game-btn--ghost"
                                onClick={handleRemoveAnnouncementImage}
                              >
                                Remove
                              </button>
                            )}
                          </div>

                          {announcementImageCrop && (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.65rem",
                              }}
                            >
                              <span
                                className="game-text-secondary"
                                style={{ fontSize: "0.78rem" }}
                              >
                                Cropping {announcementImageCrop.fileName}
                              </span>
                              <label className="game-label">
                                Zoom
                                <input
                                  type="range"
                                  min="1"
                                  max="3"
                                  step="0.01"
                                  value={announcementImageCrop.zoom}
                                  onChange={(e) =>
                                    setAnnouncementImageCrop((current) => ({
                                      ...current,
                                      zoom: Number(e.target.value),
                                    }))
                                  }
                                  style={{ width: "100%" }}
                                />
                              </label>
                              <label className="game-label">
                                Horizontal
                                <input
                                  type="range"
                                  min="-100"
                                  max="100"
                                  step="1"
                                  value={announcementImageCrop.offsetX}
                                  onChange={(e) =>
                                    setAnnouncementImageCrop((current) => ({
                                      ...current,
                                      offsetX: Number(e.target.value),
                                    }))
                                  }
                                  style={{ width: "100%" }}
                                />
                              </label>
                              <label className="game-label">
                                Vertical
                                <input
                                  type="range"
                                  min="-100"
                                  max="100"
                                  step="1"
                                  value={announcementImageCrop.offsetY}
                                  onChange={(e) =>
                                    setAnnouncementImageCrop((current) => ({
                                      ...current,
                                      offsetY: Number(e.target.value),
                                    }))
                                  }
                                  style={{ width: "100%" }}
                                />
                              </label>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "0.6rem",
                                  flexWrap: "wrap",
                                }}
                              >
                                <button
                                  type="button"
                                  className="game-btn game-btn--primary"
                                  onClick={handleApplyAnnouncementImageCrop}
                                >
                                  Apply Crop
                                </button>
                                <button
                                  type="button"
                                  className="game-btn game-btn--ghost"
                                  onClick={() => {
                                    setAnnouncementImageCrop(null);
                                    setAnnouncementImageError("");
                                  }}
                                >
                                  Cancel Crop
                                </button>
                              </div>
                            </div>
                          )}

                          {announcementImageError && (
                            <p
                              className="game-text-danger"
                              style={{ margin: 0, fontSize: "0.8rem" }}
                            >
                              {announcementImageError}
                            </p>
                          )}
                        </div>
                      </div>

                      <div>
                        <textarea
                          className="game-input"
                          value={cardroomData.announcementText || ""}
                          onChange={(e) =>
                            setCardroomData({
                              ...cardroomData,
                              announcementText: e.target.value.slice(0, 255),
                            })
                          }
                          rows={3}
                          maxLength={255}
                          placeholder="Optional text shown under the announcement image."
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            resize: "vertical",
                            fontFamily: "inherit",
                            fontSize: "0.85rem",
                          }}
                        />
                        <p
                          className="game-text-secondary"
                          style={{
                            margin: "0.35rem 0 0",
                            fontSize: "0.75rem",
                            textAlign: "right",
                          }}
                        >
                          {(cardroomData.announcementText || "").length}/255
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    className="settings-field settings-field--general"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "center",
                      padding: "0.9rem 0",
                      borderBottom: "1px solid var(--game-border-light)",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Default Max Players
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        Default seat count for newly created tables.
                      </p>
                    </div>
                    <select
                      className="game-input"
                      value={cardroomData.default_max_players}
                      onChange={(e) =>
                        setCardroomData({
                          ...cardroomData,
                          default_max_players: parseInt(e.target.value) || 0,
                        })
                      }
                      required
                      style={{ width: "100%", boxSizing: "border-box" }}
                    >
                      {[5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div
                    className="settings-field settings-field--general"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "center",
                      padding: "0.9rem 0",
                      borderBottom: "1px solid var(--game-border-light)",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Waitlist Duration
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        How long players can stay active on waitlists.
                      </p>
                    </div>
                    <select
                      className="game-input"
                      value={
                        cardroomData.waitlistDuration === null
                          ? "none"
                          : cardroomData.waitlistDuration
                      }
                      onChange={(e) =>
                        setCardroomData({
                          ...cardroomData,
                          waitlistDuration:
                            e.target.value === "none"
                              ? null
                              : parseInt(e.target.value),
                        })
                      }
                      style={{ width: "100%", boxSizing: "border-box" }}
                    >
                      <option value="none">None (Unlimited)</option>
                      {[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60].map(
                        (n) => (
                          <option key={n} value={n}>
                            {n} Minutes
                          </option>
                        ),
                      )}
                    </select>
                  </div>

                  <div
                    className="settings-field settings-field--cash"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "center",
                      padding: "0.9rem 0",
                      borderBottom: "1px solid var(--game-border-light)",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Default Credit Limit
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        Automatically assign a session credit limit to newly
                        added players.
                      </p>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "0.75rem",
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <select
                        className="game-input"
                        value={
                          cardroomData.defaultCreditLimit === null ||
                          cardroomData.defaultCreditLimit === undefined
                            ? "off"
                            : "on"
                        }
                        onChange={(e) =>
                          setCardroomData({
                            ...cardroomData,
                            defaultCreditLimit:
                              e.target.value === "off" ? null : 0,
                          })
                        }
                        style={{ flex: "1 1 150px", boxSizing: "border-box" }}
                      >
                        <option value="off">Off</option>
                        <option value="on">On</option>
                      </select>
                      <input
                        type="number"
                        className="game-input"
                        value={cardroomData.defaultCreditLimit ?? ""}
                        onChange={(e) =>
                          setCardroomData({
                            ...cardroomData,
                            defaultCreditLimit:
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value).toString(),
                          })
                        }
                        min="0"
                        step="1"
                        placeholder="0"
                        disabled={
                          cardroomData.defaultCreditLimit === null ||
                          cardroomData.defaultCreditLimit === undefined
                        }
                        style={{ flex: "1 1 150px", boxSizing: "border-box" }}
                      />
                    </div>
                  </div>

                  <div
                    className="settings-field settings-field--nexus-mobile"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "center",
                      padding: "0.9rem 0",
                      borderBottom: "1px solid var(--game-border-light)",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Club Joining
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        Choose whether mobile players need approval.
                      </p>
                    </div>
                    <select
                      className="game-input"
                      value={cardroomData.isOpen}
                      onChange={(e) =>
                        setCardroomData({
                          ...cardroomData,
                          isOpen: parseInt(e.target.value),
                        })
                      }
                      style={{ width: "100%", boxSizing: "border-box" }}
                    >
                      <option value={1}>Anyone can join</option>
                      <option value={0}>Approval required</option>
                    </select>
                  </div>

                  <div
                    className="settings-field settings-field--nexus-mobile"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "center",
                      padding: "0.9rem 0",
                      borderBottom: "1px solid var(--game-border-light)",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Mobile Network
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        Let mobile players find your club, view live waitlists,
                        and request to join games.
                      </p>
                    </div>
                    {!cardroomData?.accessCode ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-start",
                        }}
                      >
                        <button
                          type="button"
                          className="game-btn"
                          style={{ backgroundColor: "#6366f1", color: "white" }}
                          onClick={() => {
                            generateCode();
                            setShowSettingsModal(false);
                            setShowNetworkModal(true);
                          }}
                        >
                          Join Mobile Network
                        </button>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "0.75rem",
                          flexWrap: "wrap",
                        }}
                      >
                        <div>
                          <span
                            style={{
                              color: "var(--game-text-secondary)",
                              fontSize: "0.75rem",
                              display: "block",
                              marginBottom: "0.25rem",
                            }}
                          >
                            Access Code
                          </span>
                          <strong
                            style={{
                              color: "#818cf8",
                              background: "rgba(99, 102, 241, 0.15)",
                              border: "1px solid rgba(99, 102, 241, 0.35)",
                              borderRadius: "6px",
                              padding: "0.35rem 0.6rem",
                              letterSpacing: "0.08em",
                              display: "inline-block",
                            }}
                          >
                            {cardroomData.accessCode}
                          </strong>
                        </div>
                        <button
                          type="button"
                          className="game-btn game-btn--secondary"
                          onClick={() => {
                            setNewAccessCode(cardroomData.accessCode);
                            setNewIsOpen(cardroomData.isOpen === 1);
                            setShowSettingsModal(false);
                            setShowNetworkModal(true);
                          }}
                        >
                          Manage Network
                        </button>
                      </div>
                    )}
                  </div>

                  <div
                    className="settings-field settings-field--nexus-mobile"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "start",
                      padding: "0.9rem 0",
                      borderBottom: "1px solid var(--game-border-light)",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Discover Listing
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        Show this cardroom in nearby mobile discovery with its
                        exact public address.
                      </p>
                      {cardroomData.discoverLat !== null &&
                        cardroomData.discoverLat !== undefined &&
                        cardroomData.discoverLng !== null &&
                        cardroomData.discoverLng !== undefined && (
                          <div
                            style={{
                              marginTop: "0.75rem",
                              padding: "0.75rem",
                              border: "1px solid var(--game-border-light)",
                              borderRadius: "8px",
                              background: "rgba(255,255,255,0.03)",
                            }}
                          >
                            <strong>{cardroomData.discoverAddress}</strong>
                            <p
                              className="game-text-secondary"
                              style={{
                                margin: "0.35rem 0 0",
                                fontSize: "0.78rem",
                                lineHeight: 1.35,
                              }}
                            >
                              {cardroomData.discoverCity}
                              {cardroomData.discoverCity &&
                              cardroomData.discoverState
                                ? ", "
                                : ""}
                              {cardroomData.discoverState} ·{" "}
                              {cardroomData.discoverLat},{" "}
                              {cardroomData.discoverLng}
                            </p>
                            {originalCardroomData?.discoverLocationChangedAt && (
                              <p
                                className="game-text-secondary"
                                style={{
                                  margin: "0.35rem 0 0",
                                  fontSize: "0.72rem",
                                }}
                              >
                                Last changed{" "}
                                {formatShortDateTime(
                                  originalCardroomData.discoverLocationChangedAt,
                                )}
                              </p>
                            )}
                          </div>
                        )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.75rem",
                      }}
                    >
                      <select
                        className="game-input"
                        value={cardroomData.discoverable ? 1 : 0}
                        onChange={(e) =>
                          setCardroomData({
                            ...cardroomData,
                            discoverable: parseInt(e.target.value),
                          })
                        }
                        style={{ width: "100%", boxSizing: "border-box" }}
                      >
                        <option value={0}>Hidden from Discover</option>
                        <option value={1}>Show in Discover</option>
                      </select>

                      {discoverEnabled && (
                        <>
                      <MapboxAddressInput
                        value={discoverAddressQuery}
                        selectedAddress={cardroomData.discoverAddress}
                        disabled={discoverLocationInputDisabled}
                        disabledReason={discoverLocationLockText}
                        onChange={(nextValue) => {
                          setDiscoverAddressQuery(nextValue);
                          setCardroomData({
                            ...cardroomData,
                            discoverAddress: "",
                            discoverCity: "",
                            discoverState: "",
                            discoverLat: null,
                            discoverLng: null,
                          });
                        }}
                        onAddressSelect={(address) => {
                          setCardroomData({
                            ...cardroomData,
                            discoverAddress: address.address,
                            discoverCity: address.city,
                            discoverState: address.state,
                            discoverLat: address.lat,
                            discoverLng: address.lng,
                          });
                          setDiscoverAddressQuery(address.address);
                        }}
                      />

                      {Boolean(cardroomData.__movedDiscoverLocation) && (cardroomData.discoverAddress ||
                        cardroomData.discoverState) && (
                        <div
                          style={{
                            padding: "0.75rem",
                            border: "1px solid var(--game-border-light)",
                            borderRadius: "8px",
                            background: "rgba(255,255,255,0.03)",
                          }}
                        >
                          <strong>{cardroomData.discoverAddress}</strong>
                          <p
                            className="game-text-secondary"
                            style={{ margin: "0.35rem 0 0", fontSize: "0.78rem" }}
                          >
                            {cardroomData.discoverCity}
                            {cardroomData.discoverCity &&
                            cardroomData.discoverState
                              ? ", "
                              : ""}
                            {cardroomData.discoverState} ·{" "}
                            {cardroomData.discoverLat},{" "}
                            {cardroomData.discoverLng}
                          </p>
                        </div>
                      )}

                      <textarea
                        className="game-input"
                        value={cardroomData.discoverText || ""}
                        onChange={(e) =>
                          setCardroomData({
                            ...cardroomData,
                            discoverText: e.target.value.slice(0, 255),
                          })
                        }
                        rows={3}
                        maxLength={255}
                        placeholder="Short welcome message shown on Discover."
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          resize: "vertical",
                          fontFamily: "inherit",
                          fontSize: "0.85rem",
                        }}
                      />
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: "-0.45rem 0 0",
                          fontSize: "0.75rem",
                          textAlign: "right",
                        }}
                      >
                        {(cardroomData.discoverText || "").length}/255
                      </p>
                        </>
                      )}
                    </div>
                  </div>

                  <div
                    className="settings-field settings-field--general"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "center",
                      padding: "0.9rem 0",
                      borderBottom: "1px solid var(--game-border-light)",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Bad Beat
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        Enable or disable bad beat tracking.
                      </p>
                    </div>
                    <select
                      className="game-input"
                      value={cardroomData.bad_beat || 0}
                      onChange={(e) =>
                        setCardroomData({
                          ...cardroomData,
                          bad_beat: parseInt(e.target.value),
                        })
                      }
                      style={{ width: "100%", boxSizing: "border-box" }}
                    >
                      <option value={1}>Allow</option>
                      <option value={0}>Disable</option>
                    </select>
                  </div>

                  <div
                    className="settings-field settings-field--nexus-mobile"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "start",
                      padding: "0.9rem 0",
                      borderBottom: "1px solid var(--game-border-light)",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Default Accept Message
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        Sent when approving a player join request.
                      </p>
                    </div>
                    <textarea
                      className="game-input"
                      value={cardroomData.defaultAcceptMessage || ""}
                      onChange={(e) =>
                        setCardroomData({
                          ...cardroomData,
                          defaultAcceptMessage: e.target.value,
                        })
                      }
                      rows={4}
                      placeholder="Message sent when approving a player..."
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        resize: "vertical",
                        fontFamily: "inherit",
                        fontSize: "0.85rem",
                      }}
                    />
                  </div>

                  <div
                    className="settings-field settings-field--nexus-mobile"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "start",
                      padding: "0.9rem 0",
                      borderBottom: "1px solid var(--game-border-light)",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Default Reject Message
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        Sent when rejecting a player join request.
                      </p>
                    </div>
                    <textarea
                      className="game-input"
                      value={cardroomData.defaultRejectMessage || ""}
                      onChange={(e) =>
                        setCardroomData({
                          ...cardroomData,
                          defaultRejectMessage: e.target.value,
                        })
                      }
                      rows={4}
                      placeholder="Message sent when rejecting a player..."
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        resize: "vertical",
                        fontFamily: "inherit",
                        fontSize: "0.85rem",
                      }}
                    />
                  </div>

                  <div
                    className="settings-field settings-field--cash"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "center",
                      padding: "0.9rem 0",
                      borderBottom: "1px solid var(--game-border-light)",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Referral Bonus ($)
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        Reward for the player who referred someone.
                      </p>
                    </div>
                    <input
                      type="number"
                      className="game-input"
                      value={cardroomData.referralBonus || 0}
                      onChange={(e) =>
                        setCardroomData({
                          ...cardroomData,
                          referralBonus: parseInt(e.target.value) || 0,
                        })
                      }
                      min="0"
                      placeholder="0"
                      style={{ width: "100%", boxSizing: "border-box" }}
                    />
                  </div>

                  <div
                    className="settings-field settings-field--cash"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "center",
                      padding: "0.9rem 0",
                      borderBottom: "1px solid var(--game-border-light)",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Referee Bonus ($)
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        Reward for the new player being referred.
                      </p>
                    </div>
                    <input
                      type="number"
                      className="game-input"
                      value={cardroomData.refereeBonus || 0}
                      onChange={(e) =>
                        setCardroomData({
                          ...cardroomData,
                          refereeBonus: parseInt(e.target.value) || 0,
                        })
                      }
                      min="0"
                      placeholder="0"
                      style={{ width: "100%", boxSizing: "border-box" }}
                    />
                  </div>

                  <div
                    className="settings-field settings-field--general"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "center",
                      padding: "0.9rem 0",
                      borderBottom: "1px solid var(--game-border-light)",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Reviews
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        View anonymous player ratings and written feedback.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="game-btn game-btn--secondary"
                      onClick={openReviewsModal}
                    >
                      View Reviews
                    </button>
                  </div>

                  <div
                    className="settings-field settings-field--cash"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "center",
                      padding: "0.9rem 0",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        First-Time Bonus ($)
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        Bonus for first-time players without referrals.
                      </p>
                    </div>
                    <input
                      type="number"
                      className="game-input"
                      value={cardroomData.firstTimeBonus || 0}
                      onChange={(e) =>
                        setCardroomData({
                          ...cardroomData,
                          firstTimeBonus: parseInt(e.target.value) || 0,
                        })
                      }
                      min="0"
                      placeholder="0"
                      style={{ width: "100%", boxSizing: "border-box" }}
                    />
                  </div>

                  <div
                    className="settings-field settings-field--general"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "1rem",
                      alignItems: "center",
                      padding: "0.9rem 0",
                    }}
                  >
                    <div>
                      <label
                        className="game-label"
                        style={{ marginBottom: "0.2rem" }}
                      >
                        Membership
                      </label>
                      <p
                        className="game-text-secondary"
                        style={{
                          margin: 0,
                          fontSize: "0.78rem",
                          lineHeight: 1.4,
                        }}
                      >
                        View billing status, renewal timing, payment methods,
                        invoices, and Stripe portal options.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="game-btn game-btn--secondary"
                      onClick={() => {
                        setMembershipPortalError("");
                        setShowSettingsModal(false);
                        setShowMembershipModal(true);
                      }}
                    >
                      Manage Membership
                    </button>
                  </div>
                </div>
              </div>

              <div className="game-modal__footer">
                <button
                  type="button"
                  className="game-btn game-btn--ghost"
                  onClick={handleCloseSettingsModal}
                  disabled={settingsLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="game-btn game-btn--primary"
                  disabled={
                    settingsLoading || settingsUnchanged || hasPendingImageCrop
                  }
                  style={{
                    opacity:
                      settingsLoading || settingsUnchanged || hasPendingImageCrop
                        ? 0.6
                        : 1,
                  }}
                >
                  {settingsLoading
                    ? "Saving..."
                    : hasPendingImageCrop
                      ? "Apply Crop First"
                      : "Update"}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
      {showTutorialModal && (
        <GameTutorialModal onClose={() => setShowTutorialModal(false)} />
      )}
      {/* Network Setup Modal */}
      {showNetworkModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setShowNetworkModal(false)}
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                {cardroomData?.accessCode
                  ? "Update Mobile Network"
                  : "Join Mobile Network"}
              </h3>
            </div>
            <div className="game-modal__body">
              <p
                style={{
                  color: "var(--game-text-secondary)",
                  fontSize: "0.85rem",
                  marginBottom: "1.5rem",
                }}
              >
                Generate a unique 4-digit access code for your cardroom. This
                code is what players will use to identify your club on the
                mobile app.
              </p>

              <div className="game-form-vertical">
                <label className="game-label">4-Digit Access Code</label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    className="game-input"
                    value={newAccessCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 4);
                      setNewAccessCode(val);
                      if (val.length === 4) checkCodeManual(val);
                    }}
                    placeholder="e.g. 1234"
                    maxLength={4}
                    required
                    style={{
                      fontSize: "1.5rem",
                      textAlign: "center",
                      letterSpacing: "0.5rem",
                      fontWeight: "bold",
                      color: codeAvailable ? "#48bb78" : "#f56565",
                    }}
                  />
                  <button
                    className="game-btn game-btn--secondary"
                    onClick={generateCode}
                    disabled={isCheckingCode}
                  >
                    Regenerate
                  </button>
                </div>
                {!codeAvailable && (
                  <p
                    style={{
                      color: "#f56565",
                      fontSize: "0.75rem",
                      marginTop: "0.4rem",
                    }}
                  >
                    Access code already in use. Please try another.
                  </p>
                )}
                {isCheckingCode && (
                  <p
                    style={{
                      color: "var(--game-text-secondary)",
                      fontSize: "0.75rem",
                      marginTop: "0.4rem",
                    }}
                  >
                    Checking availability...
                  </p>
                )}

                <div className="game-modal__footer">
                  <button
                    className="game-btn game-btn--ghost"
                    onClick={() => setShowNetworkModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="game-btn"
                    style={{
                      backgroundColor: "#48bb78",
                      color: "white",
                      opacity:
                        !codeAvailable || newAccessCode.length !== 4 ? 0.5 : 1,
                    }}
                    disabled={
                      !codeAvailable ||
                      newAccessCode.length !== 4 ||
                      networkLoading
                    }
                    onClick={handleJoinNetwork}
                  >
                    {networkLoading
                      ? "Saving..."
                      : cardroomData?.accessCode
                        ? "Update Code"
                        : "Join Network"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
